import { wasRetriedByAuthHandler } from '@/utils/http/authRetry';
import { logger } from '@/utils/logger/logger';
import { type AxiosError, type AxiosRequestConfig } from 'axios';
import { isNetworkOrIdempotentRequestError } from 'axios-retry';

const RECORDING_ID_REQUEST_TIMEOUT_MS = 30 * 1000;
const RECORDING_ID_REQUEST_RETRIES = 3;

/**
 * Thrown when every retry failed. A caller that reports failures tells this
 * apart from a single failed attempt by type, so one failed start is one event.
 */
export class RecordingIdRetriesExhaustedError extends Error {
  constructor() {
    super('Failed to generate an id, please try again later');
    this.name = 'RecordingIdRetriesExhaustedError';
  }
}

export const isRecordingIdRetriesExhausted = (
  error: unknown
): error is RecordingIdRetriesExhaustedError =>
  error instanceof RecordingIdRetriesExhaustedError;

/**
 * Retry only a network failure with no response, so the server cannot have
 * created a recording. A response lost after creation replays into a second
 * recording id and orphans the first; the client only uses an id from a
 * response it received. Same rule as the web recorder's start request
 * (`shouldRetryChunkRecordingStart` in `chunkAudioRecorder.ts`); keep the two in
 * step until they share one predicate.
 *
 * Two exclusions: a config the auth handler already replayed (a second replay
 * makes the next 401 read as session-dead and logs the user out), and a failure
 * before dispatch such as confirmed offline, which retrying only delays.
 */
const shouldRetryRecordingIdRequest = (sessionId: string, error: AxiosError) => {
  if (wasRetriedByAuthHandler(error)) {
    logger.warn('recording_id_request_retry_suppressed_after_auth_retry', {
      sessionId,
      httpStatus: error.response?.status,
      axiosErrorCode: error.code,
    });
    return false;
  }
  if (error.code === 'ERR_NETWORK' && error.request == null) {
    return false;
  }
  return isNetworkOrIdempotentRequestError(error);
};

/**
 * Axios config (the third argument of `post`) for the v1 request that mints a
 * recording id. Pass it as config: the second argument is the request body, so
 * options placed there never reach axios.
 *
 * `timeoutMs` bounds one attempt. A timeout is not retried (the server may have
 * minted the id), so a slow server fails the request after `timeoutMs`. Only a
 * network failure with a request is retried, up to 3 times, so a caller that
 * must fail fast also passes a `signal` that caps the whole request.
 */
export const recordingIdRequestConfig = (
  sessionId: string,
  options: { signal?: AbortSignal; timeoutMs?: number } = {}
): AxiosRequestConfig => ({
  timeout: options.timeoutMs ?? RECORDING_ID_REQUEST_TIMEOUT_MS,
  signal: options.signal,
  'axios-retry': {
    retries: RECORDING_ID_REQUEST_RETRIES,
    retryCondition: (error: AxiosError) =>
      shouldRetryRecordingIdRequest(sessionId, error),
    onMaxRetryTimesExceeded: (error: AxiosError) => {
      // The throw replaces the AxiosError as the rejection, so log the cause first.
      logger.error('recording_id_request_retries_exhausted', {
        sessionId,
        httpStatus: error.response?.status,
        axiosErrorCode: error.code,
      });
      throw new RecordingIdRetriesExhaustedError();
    },
  },
});
